import {
  Stack,
  AvatarGroup,
  Box,
  Grid,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Link,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Cell } from 'react-table';
import {
  getMyTeams,
  getAllWorkspaceTeams,
  getAllTeamMemberRecordsOfTeam,
  ITeamMembersResponse,
  ITeam,
  getRecruiterByEmail,
  IUserDTO,
} from 'wisestep-api';
import { WsDrawer, WsDataTable } from 'wisestep-base';

import { openUrlInNewTab } from '../../utils/openUrlInNewTab';

type TeamAvatarProps = {
  owner?: boolean;
  defaultTeam?: ITeam;
};

const RecruiterAvatarSurplus = ({ list, skip, openTeamDrawer, ...props }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          palette: {
            mode: 'light',
          },
        })
      }
    >
      {list?.length > skip && (
        <Avatar
          {...props}
          onClick={handleClick}
          sx={{
            ...props.sx,
            cursor: 'pointer',
            bgcolor: '#CAF0F8',
            color: 'common.black',
            height: avatarSize,
            width: avatarSize,
            fontSize: avatarSize / 2,
          }}
        >
          +{list?.length - skip}
        </Avatar>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List>
          {list
            ?.filter((e, i) => i >= skip)
            .map((listItem, i) => (
              <ListItem key={i} disablePadding>
                <ListItemButton onClick={() => openTeamDrawer(listItem)}>
                  <ListItemText primary={listItem.name} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Popover>
    </ThemeProvider>
  );
};

const avatarSize = 30;

const getIdByEmail = async (email) => {
  const response = await getRecruiterByEmail(email);
  const userRes: IUserDTO = response.data;
  openUrlInNewTab(
    `/recruiter/profile?id=${String(userRes.id)}&email=${email}&isLink=true&isOverLap=yes`
  );
};

const TeamAvatartGroup = ({ owner = false, defaultTeam }: TeamAvatarProps) => {
  const [teamList, setTeamList] = useState<ITeam[]>([]);
  const [openTeamMemberList, setOpenTeamMemberList] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<ITeamMembersResponse[]>();
  const [selectedTeam, setSelectedTeam] = useState<ITeam | null>(defaultTeam ?? null);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Email',
        accessor: 'memberEmail',
        Cell: (cell: Cell) => (
          <Grid container xs={12}>
            <Link
              underline="none"
              onClick={() => {
                getIdByEmail(cell.value);
              }}
              sx={{ cursor: 'pointer' }}
            >
              {cell.value}
            </Link>
          </Grid>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    async function fetchData() {
      const response = owner ? await getAllWorkspaceTeams() : await getMyTeams();
      setTeamList(response.data);
    }
    fetchData();
  }, [owner]);

  const openTeamDrawer = useCallback((team: ITeam) => {
    getMembersOfTeam(team);
    setOpenTeamMemberList(true);
  }, []);

  useEffect(() => defaultTeam && openTeamDrawer(defaultTeam), [defaultTeam, openTeamDrawer]);

  const closeDrawer = () => {
    setOpenTeamMemberList(false);
    setTeamMembers([]);
    setSelectedTeam(null);
  };

  function stringToColor(string: string) {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  }

  function stringAvatar(name: string, index?: number) {
    return {
      sx: {
        bgcolor:
          index !== undefined
            ? ['#F6EBD0', '#FDC9D0', '#BBF2EF', '#CAF0F8'][index]
            : stringToColor(name),
        color: 'common.black',
      },
    };
  }

  const getMembersOfTeam = async (team: ITeam) => {
    const teamId = team.id;
    const teamMembers: ITeamMembersResponse = {
      memberEmail: team.ownerEmail ?? '',
      name: '',
      role: 'owner',
      roles: null,
      teamId: team.id ?? '',
    };
    if (teamId) {
      const response = await getAllTeamMemberRecordsOfTeam(teamId);
      setTeamMembers([teamMembers, ...response.data.teamMembers]);
      setSelectedTeam(team);
    } else {
      setTeamMembers([teamMembers]);
      setSelectedTeam(team);
    }
  };

  const data = useMemo(
    () =>
      teamMembers?.map((user) => ({
        memberEmail: user.memberEmail,
        name: user.name,
      })),
    [teamMembers]
  );

  const maxIcon = 4;
  const getName = (team: any) => {
    return (team?.name ?? '')
      .split(' ')
      .map((nameSubStr, index) => {
        if (index < 2) return nameSubStr[0];
      })
      .join('');
  };
  return (
    <Box>
      {isEmpty(defaultTeam) && (
        <Grid item>
          <Stack direction="row" spacing={2}>
            <AvatarGroup max={maxIcon} sx={{ maxWidth: '10.5rem', justifyContent: 'flex-end' }}>
              {teamList &&
                teamList.map((team, index) => {
                  const surplus = index === 3 && teamList.length > 4;
                  const name = getName(team).toUpperCase();
                  const props = { ...stringAvatar(name, index) };
                  props.sx['width'] = avatarSize;
                  props.sx['height'] = avatarSize;
                  props.sx['fontSize'] = avatarSize / 2;
                  return (
                    index <= 3 &&
                    (surplus ? (
                      <RecruiterAvatarSurplus
                        list={teamList}
                        openTeamDrawer={openTeamDrawer}
                        {...props}
                        skip={3}
                      />
                    ) : (
                      <Tooltip key={index} title={team.name ?? 'Unknown'}>
                        <Avatar
                          {...props}
                          onClick={(e: any) => {
                            openTeamDrawer(team);
                            if (index + 1 < maxIcon) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                        >
                          {name}
                        </Avatar>
                      </Tooltip>
                    ))
                  );
                })}
            </AvatarGroup>
          </Stack>
        </Grid>
      )}
      {teamMembers && teamMembers.length > 0 && openTeamMemberList && (
        <>
          <WsDrawer
            title={`${selectedTeam?.name} - Team members`}
            open={openTeamMemberList}
            size="compact"
            onClose={closeDrawer}
          >
            <Grid container>
              <WsDataTable
                columns={columns}
                selectable={false}
                data={data ?? []}
                boldItems={['email']}
              />
            </Grid>
          </WsDrawer>
        </>
      )}
    </Box>
  );
};

export default TeamAvatartGroup;
